import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

interface DropdownOptions {
  [key: string]: string;
}

@Component({
  selector: 'ut-custom-dropdown',
  templateUrl: './ut-custom-dropdown.component.html',
  styleUrls: ['./ut-custom-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UtCustomDropdownComponent implements OnInit {
  @Input() options: DropdownOptions = {};
  @Input() model: string = '';
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onChange = new EventEmitter<string>();

  @ViewChild('triggerButton', { static: true })
    triggerButton: ElementRef<HTMLButtonElement>;

  dropdownShown = false;
  label = '';
  selectedItemValue = '';
  optionKeys: string[] = [];

  // Key codes for keyboard navigation
  private readonly KEY_CODES = {
    TAB: 9,
    RETURN: 13,
    ESC: 27,
    SPACE: 32,
    UP: 38,
    DOWN: 40,
  };

  constructor(
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Initialize dropdown state
    this.optionKeys = Object.keys(this.options);

    if (!this.model && this.optionKeys.length > 0) {
      this.model = this.optionKeys[0];
      this.selectedItemValue = this.optionKeys[0];
    } else {
      this.selectedItemValue = this.model;
    }

    this.label = this.options[this.model] || '';
  }

  toggleDropdown(): void {
    this.dropdownShown = !this.dropdownShown;

    if (this.dropdownShown) {
      this.cdr.markForCheck();
      // Focus selected item after dropdown opens
      setTimeout(() => {
        this.focusSelectedItem();
      }, 10);
    } else {
      this.selectedItemValue = this.model;
      this.triggerButton.nativeElement.focus();
      this.cdr.markForCheck();
    }
  }

  setValue(value: string): void {
    const oldValue = this.model;
    this.label = this.options[value];
    this.model = value;
    this.dropdownShown = false;

    if (oldValue !== value) {
      this.onChange.emit(value);
    }

    this.triggerButton.nativeElement.focus();
    this.cdr.markForCheck();
  }

  onOptionClick(value: string): void {
    this.setValue(value);
  }

  onOptionKeydown(event: KeyboardEvent, value: string): void {
    const codes = [
      this.KEY_CODES.TAB,
      this.KEY_CODES.RETURN,
      this.KEY_CODES.ESC,
      this.KEY_CODES.SPACE,
      this.KEY_CODES.UP,
      this.KEY_CODES.DOWN,
    ];

    if (codes.includes(event.keyCode)) {
      switch (event.keyCode) {
        case this.KEY_CODES.SPACE:
        case this.KEY_CODES.RETURN:
          event.preventDefault();
          this.setValue(value);
          break;
        case this.KEY_CODES.UP:
          event.preventDefault();
          this.focusNextListItem('up');
          break;
        case this.KEY_CODES.DOWN:
          event.preventDefault();
          this.focusNextListItem('down');
          break;
        case this.KEY_CODES.ESC:
        case this.KEY_CODES.TAB:
          this.toggleDropdown();
          break;
        default:
          break;
      }
    }
  }

  onOptionBlur(event: FocusEvent): void {
    // Close dropdown if focus moves outside of dropdown area
    if (event.relatedTarget === null) {
      this.toggleDropdown();
    }
  }

  @HostListener('keydown.escape')
  onEscapeKey(): void {
    if (this.dropdownShown) {
      this.toggleDropdown();
    }
  }

  private focusNextListItem(direction: 'up' | 'down'): void {
    const currentItemIndex = this.optionKeys.indexOf(this.selectedItemValue);
    let nextIndex: number;

    if (direction === 'down') {
      nextIndex = currentItemIndex < (this.optionKeys.length - 1)
        ? currentItemIndex + 1
        : currentItemIndex;
    } else {
      nextIndex = currentItemIndex > 0
        ? currentItemIndex - 1
        : currentItemIndex;
    }

    if (nextIndex !== currentItemIndex) {
      this.selectedItemValue = this.optionKeys[nextIndex];
      const nextListItem = document.getElementById(`filter-option-${nextIndex}`);
      if (nextListItem) {
        nextListItem.focus();
      }
    }
  }

  private focusSelectedItem(): void {
    const currentItemIndex = this.optionKeys.indexOf(this.selectedItemValue);
    const selectedItem = document.getElementById(`filter-option-${currentItemIndex}`);
    if (selectedItem) {
      selectedItem.focus();
    }
  }
}
